import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { Table } from 'reactstrap'

import { API } from 'API'
import { RequestToasterContext } from 'containers/Providers'
import AccessRolesControl from 'containers/UsersPage/components/UserInfo/AccessRolesControl'
import DepartmentsControl from 'containers/UsersPage/components/UserInfo/DepartmentsControl'
import { format } from 'date-fns'
import { FeatureFlags } from 'domains/featureFlags'
import { LanguageRegions } from 'domains/languageRegions'
import { ForbiddenUserRoles, User, UserOperatorRoles, UserRoles } from 'domains/user'
import { StandardErrorResponse } from 'types/APIResponses'
import { isNotNil } from 'utils/isNotNil'

import { AccessPermissionsControl } from './AccessPermissionsControl'
import { CountriesPermissionsControl } from './CountriesPermissionsControl'
import { PermissionsControl } from './PermissionsControl'

interface UserInfoProps {
  loggedUserName: string
  loggedUserFeatureFlags: FeatureFlags
  loggedUserAccessRoles: string
  user: User
  getUser: () => void
  featureFlags: FeatureFlags
}

const ODB_WORKFLOW_TYPE = 'odb'

export const UserInfo = ({
  user,
  getUser,
  loggedUserName,
  loggedUserFeatureFlags,
  loggedUserAccessRoles,
  featureFlags,
}: UserInfoProps) => {
  const {
    username,
    federatedUser,
    failedLoginCount,
    firstLoginAttemptDate,
    firstName,
    lastName,
    accessRoles,
    accessPermissions,
    adminDepartments,
    enabled,
  } = user

  const { requestStatusRef } = useContext(RequestToasterContext)

  const [password, setPassword] = useState('')
  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPassword(event.target.value)

  const [isUpdating, setIsUpdating] = useState(false)
  const [userRole, setUserRole] = useState<UserRoles | ''>(user.userRole ?? '')
  const [workflowTypes, setWorkflowTypes] = useState<string[]>(user.workflowTypes ?? [])

  const handleChangeRole = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const userRole = event.target.value as UserRoles

    if (Object.keys(ForbiddenUserRoles).includes(userRole) || userRole == UserRoles.ROLE_NORMAL) {
      setOperatorRole('')
    } else if (userRole == UserRoles.ROLE_OPERATOR) {
      setOperatorRole(UserOperatorRoles.ROLE_SCANNER)
    }
    setUserRole(userRole)
  }

  const [operatorRole, setOperatorRole] = useState<UserOperatorRoles | ''>(
    user.userOperatorRole ?? '',
  )
  const handleChangeOperatorRole = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setOperatorRole(event.target.value as UserOperatorRoles)

  useEffect(() => {
    setUserRole(user.userRole ?? '')
    setOperatorRole(user.userOperatorRole ?? '')
    setWorkflowTypes(user.workflowTypes ?? [])
  }, [user])

  const [languageRegion, setLanguageRegion] = useState<LanguageRegions>(user.languageRegion ?? '')
  const handleChangeSetLanguageRegion = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setLanguageRegion(event.target.value)

  const isStatusChangeAllowed =
    (loggedUserFeatureFlags?.FeatureFlags?.adminStatusChangeEnabled ?? false) &&
    loggedUserName !== username
  const isUserRoleDisabled = federatedUser || loggedUserName === username
  const isUserOperatorRoleDisabled = isUserRoleDisabled || userRole !== UserRoles.ROLE_OPERATOR
  const isRolesUpdateDisabled =
    isUserRoleDisabled ||
    !userRole ||
    userRole === UserRoles.ROLE_NORMAL ||
    !Object.values(UserRoles).includes(userRole)
  const DSSOFRRole = ['ROLE_DIGITAL_REP', 'ROLE_FIELD_REP']
  const isDSSOFREnabled = featureFlags?.dssOfr ?? false

  const handleUpdateRole = () => {
    API.updateUser({
      username: username,
      userRole: userRole || null,
      userOperatorRole: operatorRole || null,
    })
      .then(() => {
        requestStatusRef.current?.showAlert('Updated', 'success')
        getUser()
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }
  const handleResetFailedLoginCount = () => {
    API.updateUser({ username, failedLoginCount: 0 })
      .then(() => {
        requestStatusRef.current?.showAlert('Updated', 'success')
        getUser()
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }
  const handleUpdateLanguageRegion = () => {
    API.updateLanguageRegion({ username, languageRegion: languageRegion ?? null })
      .then(() => {
        requestStatusRef.current?.showAlert('Updated', 'success')
        getUser()
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }
  const handleUpdatePassword = () => {
    requestStatusRef.current?.startProgress('Resetting user password...', 'secondary')
    API.resetPassword({ username, password })
      .then(() => {
        requestStatusRef.current?.showAlert('Updated', 'success')
        getUser()
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  const validateRole = (role: string) => {
    return isDSSOFREnabled ? true : DSSOFRRole.indexOf(role) < 0
  }

  const handleChangeWorkflow = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked

    if (value) {
      setWorkflowTypes([...workflowTypes, ODB_WORKFLOW_TYPE])
    } else {
      setWorkflowTypes(workflowTypes.filter((item) => item !== ODB_WORKFLOW_TYPE))
    }
  }

  const handleChangeStatus = () => {
    if (!isStatusChangeAllowed) return

    setIsUpdating(true)
    API.updateUser({ username, enabled: !enabled })
      .then(() => window.location.reload())
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
        setIsUpdating(false)
      })
  }

  const handleUpdateWorkflow = () => {
    API.updateUser({ username, workflowTypes: JSON.stringify(workflowTypes) })
      .then(() => {
        requestStatusRef.current?.showAlert('Updated', 'success')
        getUser()
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  return (
    <>
      <Table bordered className="w-75">
        <tbody>
          <tr>
            <td>User</td>
            <td>
              <span className="mr-1">
                | {firstName} {lastName}
              </span>
              {federatedUser && <span className="mr-1">(Federated)</span>}
            </td>
          </tr>
          <tr>
            <td>User Status</td>
            <td>
              <span className="mr-1">{enabled ? 'Active' : 'Inactive'}</span>
              {userRole === 'ROLE_USER_ADMIN' && (
                <button
                  onClick={handleChangeStatus}
                  className="float-right"
                  type="button"
                  disabled={!isStatusChangeAllowed || isUpdating}>
                  {enabled ? 'Deactivate' : 'Reactivate'}
                </button>
              )}
            </td>
          </tr>
          {!(featureFlags.asAdminLogInUsingAzureActiveDirectory ?? false) && (
            <tr>
              <td>Role</td>
              <td>
                <form>
                  <select
                    name="userRole"
                    disabled={isUserRoleDisabled}
                    value={userRole}
                    onChange={handleChangeRole}
                    className="w-50 d-block">
                    {Object.values(UserRoles).map(
                      (role, index) =>
                        validateRole(role) && (
                          <option value={role} key={`${role}-${index}`}>
                            {role}
                          </option>
                        ),
                    )}
                  </select>
                  <select
                    name="userOperatorRole"
                    disabled={isUserOperatorRoleDisabled}
                    value={operatorRole}
                    onChange={handleChangeOperatorRole}
                    className="w-50 d-blockk mt-2">
                    {Object.values(UserOperatorRoles).map((role, index) => (
                      <option value={role} key={`${role}-${index}`}>
                        {role}
                      </option>
                    ))}
                    {(userRole === 'ROLE_USER_ADMIN' || userRole === 'ROLE_NORMAL') && (
                      <option value="">null</option>
                    )}
                  </select>
                  <button
                    disabled={isRolesUpdateDisabled}
                    onClick={handleUpdateRole}
                    className="float-right"
                    type="button">
                    Update
                  </button>
                </form>
              </td>
            </tr>
          )}
          <tr>
            <td>Failed logins</td>
            <td>
              <span className="mr-1">Count: {failedLoginCount}</span>
              {isNotNil(firstLoginAttemptDate) && (
                <span className="mr-1">
                  | First attempt: {format(firstLoginAttemptDate, 'dd-MM-yyyy')}
                </span>
              )}
              <button onClick={handleResetFailedLoginCount} className="float-right" type="button">
                Reset
              </button>
            </td>
          </tr>
          <tr>
            <td>Reset Password</td>
            <td>
              <input
                type="password"
                value={password}
                className="w-50 mr-2"
                placeholder="Enter password"
                onChange={handleChangePassword}
              />
              <button
                onClick={handleUpdatePassword}
                className="float-right"
                type="button"
                disabled={federatedUser}>
                Reset
              </button>
            </td>
          </tr>
          <tr>
            <td>Language Region</td>
            <td>
              <form>
                <select
                  name="languageRegion"
                  value={languageRegion}
                  onChange={handleChangeSetLanguageRegion}
                  className="w-50">
                  {Object.values(LanguageRegions).map((region, index) => (
                    <option value={region} key={`${region}-${index}`}>
                      {region}
                    </option>
                  ))}
                  <option value="">null</option>
                </select>
                <button onClick={handleUpdateLanguageRegion} className="float-right">
                  Update
                </button>
              </form>
            </td>
          </tr>
          <tr>
            <td>Workflow permissions</td>
            <td className="w-75">
              <p>Supported Workflows</p>
              <form>
                <label className="mb-0">
                  <input
                    type="checkbox"
                    checked={workflowTypes.includes(ODB_WORKFLOW_TYPE)}
                    onChange={handleChangeWorkflow}
                    style={{ marginRight: '0.5rem' }}
                  />
                  ODB
                </label>
                <button onClick={handleUpdateWorkflow} className="float-right">
                  Update
                </button>
              </form>
            </td>
          </tr>
          <tr>
            <td>Feature Permissions</td>

            <td className="w-75">
              <PermissionsControl username={username} />
            </td>
          </tr>
          {user.userRole === UserRoles.ROLE_USER_ADMIN && (
            <>
              <tr>
                <td>Access Departments</td>
                <td className="w-75">
                  <small style={{ fontSize: '0.8rem', color: '#888' }}>
                    Selecting departments assigns the User to one or more organizational
                    departments. For Admin and Super Admin users, selected departments also define
                    which users they are responsible for managing.
                  </small>
                  <DepartmentsControl
                    username={username}
                    adminDepartments={adminDepartments}
                    actorAccessRoles={loggedUserAccessRoles}
                    targetAccessRoles={accessRoles}
                    onUpdated={getUser}
                  />
                </td>
              </tr>
              <tr>
                <td>Access Roles</td>
                <td className="w-75">
                  <AccessRolesControl
                    username={username}
                    accessRoles={accessRoles}
                    onUpdated={getUser}
                  />
                </td>
              </tr>
            </>
          )}
          <tr>
            <td>Access Permissions</td>

            <td className="w-75">
              <AccessPermissionsControl
                username={username}
                role={userRole}
                permissions={accessPermissions}
                onUpdated={getUser}
              />
            </td>
          </tr>

          <tr>
            <td>Countries Permissions</td>

            <td className="w-75">
              <CountriesPermissionsControl username={username} userRole={user.userRole} />
            </td>
          </tr>
        </tbody>
      </Table>
    </>
  )
}
